import { styled } from '@mui/material/styles';
import useMeasure from 'react-use-measure';

export type DoughnutLayoutProps = {
  chart: React.ReactNode | React.ReactElement;
  legend: React.ReactNode;
  'aria-label'?: string;
};

const DoughnutChartRoot = styled('div')(({ theme }) => ({
  height: '100%',
  containerType: 'size',

  //Row Layout
  '@container (aspect-ratio > 1.5)': {
    [`& ${DoughnutChartLayout}`]: {
      flexDirection: 'row',
    },

    [`& ${DoughnutLegendSizer}`]: {
      paddingTop: 0,
      maxWidth: theme.spacing(250),
    },
  },
}));

const DoughnutChartLayout = styled('div')(({ theme }) => ({
  height: '100%',
  display: 'flex',

  gap: theme.spacing(10),
  flexDirection: 'column',
}));

const DoughnutChartBox = styled('div')({
  position: 'relative',
  flex: 0,
});

const DoughnutLegendBox = styled('div')(() => ({
  flex: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

const DoughnutLegendSizer = styled('div')(({ theme }) => ({
  flexBasis: theme.spacing(300),
  paddingTop: theme.spacing(10),
}));

export const DoughnutLayout = ({ chart, legend, ...props }: DoughnutLayoutProps) => {
  const [containerRef, containerBound] = useMeasure();
  const [legendRef, legendBounds] = useMeasure();

  const availableHeight = containerBound.height - legendBounds.height;
  const chartHeight = Math.min(availableHeight, containerBound.width);

  const availableWidth = containerBound.width - legendBounds.width;
  const chartWidth = Math.min(availableWidth, containerBound.height);

  const orientation = containerBound.width / containerBound.height > 1.5 ? 'row' : 'column';

  const chartSize = orientation === 'row' ? chartWidth : chartHeight;

  const isMeasured = availableHeight > 0 && availableWidth > 0;

  return (
    <DoughnutChartRoot role="figure" {...props}>
      <DoughnutChartLayout ref={containerRef}>
        <DoughnutChartBox sx={{ flexBasis: chartSize }}>
          <div style={{ position: 'absolute', inset: 0 }}>{isMeasured && chart}</div>
        </DoughnutChartBox>
        <DoughnutLegendBox>
          <DoughnutLegendSizer ref={legendRef}>{isMeasured && legend}</DoughnutLegendSizer>
        </DoughnutLegendBox>
      </DoughnutChartLayout>
    </DoughnutChartRoot>
  );
};
